import { DeleteOutlined, EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useTheme } from '@mui/material/styles'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import type { JSX } from 'react'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import type { Component } from 'types/components'
import { ComponentSourceType, ComponentType } from 'types/components'
import type { KeyedObject } from 'types/root'

interface ColumnProps {
  id: string | null
  label: string
  minWidth: number
  align?: 'right' | 'left' | 'inherit' | 'center' | 'justify' | undefined
  format?: (value: any, row: Component) => string | boolean | JSX.Element
}

interface ComponentsTableProps {
  components: Component[]
  page: number
  rowsPerPage: number
  sort: string | null
  order: string
  handleChangePage: (event: unknown, newPage: number) => void
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleSort: (property: string) => void
  totalItems: number
  showActionsColumn?: boolean
  selectable?: boolean
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  onArchiveClick?: (component: Component) => void
}

const ComponentsTable: React.FC<ComponentsTableProps> = ({
  components,
  page,
  rowsPerPage,
  sort,
  order,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSort,
  totalItems,
  showActionsColumn = true,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onArchiveClick,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const allSelected = components.length > 0 && components.every((c) => c.id != null && selectedIds.includes(c.id))
  const someSelected = components.some((c) => c.id != null && selectedIds.includes(c.id)) && !allSelected

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange?.([])
    } else {
      onSelectionChange?.(components.filter((c) => c.id != null).map((c) => c.id as string))
    }
  }

  const handleToggleRow = (id: string) => {
    const newSelected = selectedIds.includes(id) ? selectedIds.filter((sid) => sid !== id) : [...selectedIds, id]
    onSelectionChange?.(newSelected)
  }

  // Row actions menu state
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = React.useState<null | HTMLElement>(null)
  const [selectedComponentForActions, setSelectedComponentForActions] = React.useState<Component | null>(null)
  const rowActionsOpen = Boolean(rowActionsAnchorEl)

  const handleOpenRowActionsMenu = (event: React.MouseEvent<HTMLElement>, component: Component) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedComponentForActions(component)
  }

  const handleCloseRowActionsMenu = () => {
    setRowActionsAnchorEl(null)
    setSelectedComponentForActions(null)
  }

  const columns: ColumnProps[] = [
    ...(selectable
      ? [
          {
            id: null,
            label: '',
            minWidth: 50,
            format: (_value: any, row: Component) => (
              <Checkbox
                checked={row.id != null && selectedIds.includes(row.id)}
                onChange={() => row.id && handleToggleRow(row.id)}
                size="small"
              />
            ),
          } as ColumnProps,
        ]
      : []),
    { id: 'label', label: 'Name', minWidth: 170 },
    {
      id: 'organizationName',
      label: 'Team',
      minWidth: 200,
      format: (value: any, row: any) => row.organizationName,
    },
    {
      id: 'isOrganizationLevel',
      label: 'Type',
      minWidth: 85,
      format: (value: any, row: Component) => {
        return value ? 'Team' : 'Individual'
      },
    },
    {
      id: 'sourceType',
      label: 'Source',
      minWidth: 85,
      format: (value: any, row: Component) => {
        return value === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE ? 'Composite' : 'Standard'
      },
    },
    {
      id: 'componentType',
      label: 'Format',
      minWidth: 85,
      format: (value: any, row: Component) => {
        switch (value) {
          case ComponentType.COMPONENT_TYPE_INTEGER:
            return 'Integer'
          case ComponentType.COMPONENT_TYPE_PERCENTAGE:
            return 'Percent'
          case ComponentType.COMPONENT_TYPE_CURRENCY:
            return 'Currency'
          case ComponentType.COMPONENT_TYPE_BOOL:
            return 'Boolean'
          case ComponentType.COMPONENT_TYPE_FLOAT:
            return 'Decimal'
          default:
            return 'None'
        }
      },
    },
    ...(showActionsColumn
      ? [
          {
            id: 'id',
            label: 'Actions',
            minWidth: 80,
            format: (_value: any, row: Component) => (
              <Tooltip title="Component Actions">
                <IconButton
                  size="medium"
                  sx={{ color: theme.palette.primary.main }}
                  onClick={(event) => handleOpenRowActionsMenu(event, row)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ]

  return (
    <Paper sx={{ mb: 3 }}>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
        <Table stickyHeader size="small" aria-label="sticky table">
          <TableHead
            sx={{
              '& th': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `2px solid ${theme.palette.divider} !important`,
              },
            }}
          >
            <TableRow>
              {columns.map((column, idx) => (
                <TableCell
                  id={`cell-col-${column.id || idx}`}
                  sx={{
                    minWidth: column.minWidth,
                    position: 'sticky !important',
                  }}
                  key={column.id || idx}
                  align={column.align}
                >
                  {selectable && column.id === null ? (
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  ) : column.id === 'id' ? (
                    column.label
                  ) : (
                    <TableSortLabel
                      active={sort === column.id}
                      direction={sort === column.id ? (order as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort(column.id as string)}
                    >
                      {column.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody key="table-body">
            {components.map((row: KeyedObject) => (
              <TableRow id={`table-row-${row.id}`} sx={{ py: 3 }} role="checkbox" tabIndex={-1} key={row.id}>
                {columns.map((column) => {
                  const value = column.id ? row[column.id] : row
                  return (
                    <TableCell id={`cell-${column.id}`} key={column.id} align={column.align}>
                      {column.format ? column.format(value, row as Component) : value}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={rowActionsAnchorEl}
        open={rowActionsOpen}
        onClose={handleCloseRowActionsMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedComponentForActions && (
          <>
            {/* ── View ── */}
            <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
              View
            </Typography>
            <MenuItem
              onClick={() => {
                const component = selectedComponentForActions
                handleCloseRowActionsMenu()
                React.startTransition(() => {
                  navigate(`/admin/components/${component.id}/edit`)
                })
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <EditOutlined />
                Edit Component
              </Box>
            </MenuItem>

            {onArchiveClick && (
              <>
                <Divider sx={{ my: 0.5 }} />

                {/* ── Danger ── */}
                <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
                  Danger
                </Typography>
                <MenuItem
                  onClick={() => {
                    const component = selectedComponentForActions
                    handleCloseRowActionsMenu()
                    onArchiveClick(component)
                  }}
                >
                  {selectedComponentForActions.isArchived ? (
                    <Box display="flex" alignItems="center" gap={1} sx={{ color: 'primary.main' }}>
                      <UndoOutlined />
                      Unarchive Component
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} sx={{ color: 'error.main' }}>
                      <DeleteOutlined />
                      Archive Component
                    </Box>
                  )}
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      <Divider />
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ px: '20px' }}>
        <AnimateButton>
          <Tooltip title="Create a new component">
            <Button
              variant="contained"
              startIcon={<PlusOutlined />}
              color="primary"
              onClick={() =>
                React.startTransition(() => {
                  navigate('/admin/components/create')
                })
              }
            >
              Component
            </Button>
          </Tooltip>
        </AnimateButton>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 200]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Paper>
  )
}

export default ComponentsTable
